import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import DayjsUtils from "@date-io/dayjs";
import tzlookup from "tz-lookup";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { motion, AnimatePresence } from "framer-motion";
import { string, bool, func, arrayOf, object } from "prop-types";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";

import { createMuiTheme } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import updateLocale from "dayjs/plugin/updateLocale";

import gtm from "@gtm/core";
import RequestButton from "@components/shared/RequestButton";
import SimpleButton from "@components/shared/SimpleButton";
import Typography from "@components/shared/Typography";
import SlotPicker from "@components/SlotPicker/SlotPicker";
import ProgressBar from "../progressBar/ProgressBar";
import ContentContainer from "./ContentContainer";
import DatePickerTextField from "@components/TestDrive/TestDriveProgress/shared/DatePickerTextField";
import Radio from "@components/shared/Radio";

import { testDriveDateUpdateUrlEvent } from "@gtm/events/testdrive";
import { postDealAction } from "@redux/actions/dealActions";
import { fetchParkopolyAvailableSlots } from "@lib/test-drive/fetchParkopolyAvailableSlots";
import {
  useIsParkopolyEnabledSelector,
  useDaysBeforeTestDriveSelector,
} from "@redux/reducers/appConfig";
import { getIsProductionMode } from "@lib/getIsProductionMode";

import {
  CTA_TD_TYPES,
  DEAL_TYPE,
  TEST_DRIVE_TYPE_ID,
  DAYJS_LOCALES_MAP,
  DAYJS_LOCALES_WEEKDAY_TRANSLATIONS,
  LOCALES,
  COUNTRIES,
} from "@shared/constants";

import {
  setTestDrivePointAction,
  setTestDriveSlotAction,
} from "@redux/actions/testDriveActions";
import { getIsCountry } from "lib/getIsCountry";
import theme from "styles/theme";

const VALID_TIMES = {
  MORNING: "morning",
  AFTERNOON: "afternoon",
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

const defaultInitialDates = Array(60)
  .fill(null)
  .map((_, i) => ({
    dailySlotPeriodList: [{ driversSlotsAvailable: 1 }],
    date: dayjs().add(i, "day"),
  }));

dayjs.updateLocale(DAYJS_LOCALES_MAP[LOCALES.PT], {
  weekdaysMin: DAYJS_LOCALES_WEEKDAY_TRANSLATIONS[LOCALES.PT],
});

const PickDateStep = ({
  affiliation,
  progressBarColor,
  isStepComplete,
  setIsStepComplete,
  isFinalStepComplete,
  testDriveType,
  resetProgress,
  initialSelectedSlot,
  testDriveLocation,
  testDriveExclusionDates,
  parkopolyAvailableSlots: initialAvailableDays,
  isSSRRendered,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const isParkopolyEnabled = useIsParkopolyEnabledSelector();
  const {
    monday: daysBeforeTDMonday,
    tuesday: daysBeforeTDTuesday,
    wednesday: daysBeforeTDWednesday,
    thursday: daysBeforeTDThursday,
    friday: daysBeforeTDFriday,
    saturday: daysBeforeTDSaturday,
    sunday: daysBeforeTDSunday,
  } = useDaysBeforeTestDriveSelector();
  const isProduction = getIsProductionMode();

  const testDriveLocationTimezone = tzlookup(
    testDriveLocation.geoLatitude,
    testDriveLocation.geoLongitude
  );

  const currentTimeForTimezone = dayjs().tz(testDriveLocationTimezone);

  const materialTheme = createMuiTheme({
    palette: {
      primary: {
        main: theme.colors.primaryOrange,
      },
    },
  });

  const getIsTimeOptionDisabled = (day, timeOption) => {
    if (isParkopolyEnabled) {
      return false;
    }

    if (dayjs(currentTimeForTimezone).isAfter(day.date, "day")) {
      return true;
    }

    if (!dayjs(currentTimeForTimezone).isSame(day.date, "day")) {
      return false;
    }

    if (timeOption === VALID_TIMES.MORNING) {
      return dayjs(currentTimeForTimezone).isAfter(
        dayjs(day.date)
          .hour(12)
          .minute(0)
          .second(0)
      );
    }

    if (timeOption === VALID_TIMES.AFTERNOON) {
      return dayjs(currentTimeForTimezone).isAfter(
        dayjs(day.date)
          .hour(20)
          .minute(0)
          .second(0)
      );
    }

    return false;
  };

  const daysConfig = currentTimeForTimezone => {
    const currentDay = dayjs(currentTimeForTimezone).day() || dayjs().day();

    let daysBeforeTD;

    switch (currentDay) {
      case 0:
        daysBeforeTD = daysBeforeTDSunday;
        break;
      case 1:
        daysBeforeTD = daysBeforeTDMonday;
        break;
      case 2:
        daysBeforeTD = daysBeforeTDTuesday;
        break;
      case 3:
        daysBeforeTD = daysBeforeTDWednesday;
        break;
      case 4:
        daysBeforeTD = daysBeforeTDThursday;
        break;
      case 5:
        daysBeforeTD = daysBeforeTDFriday;
        break;
      case 6:
        daysBeforeTD = daysBeforeTDSaturday;
        break;
      default:
        daysBeforeTD = 1;
    }
    return daysBeforeTD;
  };

  const initialSelectedDay = isParkopolyEnabled
    ? initialSelectedSlot &&
      initialAvailableDays?.find(day =>
        dayjs(initialSelectedSlot.startDateTime).isSame(dayjs(day.date), "day")
      )
    : initialSelectedSlot?.startDateTime;

  const initialDay = isParkopolyEnabled
    ? initialSelectedDay ??
      initialAvailableDays.find(day =>
        day.dailySlotPeriodList.some(slot => slot.driversSlotsAvailable > 0)
      )
    : {
        date:
          initialSelectedDay ??
          dayjs().add(daysConfig(currentTimeForTimezone) + 1, "day"),
      };

  const [previewAvailableDays, setPreviewAvailableDays] = useState(
    initialAvailableDays ?? defaultInitialDates
  );

  const [availableDays, setAvailableDays] = useState(
    initialAvailableDays ?? defaultInitialDates
  );

  const isDayExcluded = date => {
    return !!(testDriveExclusionDates ?? []).find(excludedDay =>
      dayjs(excludedDay).isSame(date, "day")
    );
  };

  const getNextAvailableDay = day => {
    const nextDay = day.date.add(1, "day");

    if (isDayExcluded(nextDay)) {
      return getNextAvailableDay({ date: nextDay });
    }

    return nextDay;
  };

  const modifiedInitialDay = () => {
    const day =
      getIsTimeOptionDisabled(initialDay, VALID_TIMES.AFTERNOON) &&
      !isParkopolyEnabled
        ? {
            date: initialDay.date.add(
              daysConfig(currentTimeForTimezone) + 1,
              "day"
            ),
          }
        : initialDay;

    if (isDayExcluded(day.date)) {
      return {
        date: getNextAvailableDay(day),
      };
    }

    return day;
  };

  const [selectedDay, setSelectedDay] = useState(modifiedInitialDay());
  const [selectedSlot, setSelectedSlot] = useState(initialSelectedSlot);
  const [isModifiedProgressDelay, setModifiedProgressDelay] = useState(false);

  let isLaMaisonWithoutEmail = false;
  if (testDriveLocation.type === TEST_DRIVE_TYPE_ID.LAMAISON) {
    if (getIsCountry(COUNTRIES.GB)) {
      isLaMaisonWithoutEmail = false;
    } else if (!testDriveLocation.email) {
      isLaMaisonWithoutEmail = true;
    }
  }

  const shouldDisableDate = date => {
    if (isDayExcluded(date)) {
      return true;
    }

    const targetAvailableDay = previewAvailableDays.find(previewAvailableDay =>
      date.isSame(dayjs(previewAvailableDay.date), "day")
    );

    if (!targetAvailableDay) {
      return true;
    }

    if (dayjs().isSame(date, "day")) {
      return true;
    }

    if (!isParkopolyEnabled) {
      const maximumDate = dayjs().add(
        daysConfig(currentTimeForTimezone),
        "days"
      );

      if (maximumDate.isAfter(date)) {
        return true;
      }

      return (
        dayjs(currentTimeForTimezone).isSame(date, "day") &&
        dayjs(currentTimeForTimezone).isAfter(dayjs(date).hour("18"), "hour")
      );
    }

    return !targetAvailableDay.dailySlotPeriodList.some(
      slot => slot.driversSlotsAvailable > 0
    );
  };

  const onMonthChange = async nextDate => {
    try {
      const nextFormattedDate = nextDate.format("YYYY-MM-DD");

      if (isParkopolyEnabled) {
        const data = await fetchParkopolyAvailableSlots(
          { location: testDriveLocation },
          { startDate: nextFormattedDate }
        );

        setPreviewAvailableDays(data);
      }
    } catch (error) {
      if (!isProduction) {
        console.error(error);
      }
    }
  };

  const onDayChange = date => {
    const targetAvailableDay = previewAvailableDays.find(previewAvailableDay =>
      date.isSame(dayjs(previewAvailableDay.date), "day")
    );

    setSelectedDay(targetAvailableDay);
    setAvailableDays(previewAvailableDays);
    setSelectedSlot(null);
  };

  const onDatePickerClose = () => {
    const selectedMonth = dayjs(selectedDay.date).month();

    if (availableDays.some(day => dayjs(day.date).month() === selectedMonth)) {
      return;
    }

    setPreviewAvailableDays(availableDays);
  };

  const onContinueButtonClick = async () => {
    dispatch(setTestDrivePointAction(testDriveLocation));
    dispatch(setTestDriveSlotAction(selectedSlot));

    return dispatch(
      postDealAction(CTA_TD_TYPES.DATE_SELECTED, DEAL_TYPE.TEST_DRIVE)
    );
  };

  const onContinueButtonClickSuccess = () => {
    setIsStepComplete(true);
  };

  const onModifyButtonClick = () => {
    setIsStepComplete(false);

    if (resetProgress) {
      resetProgress();
    }
  };

  const onTimeSpanRadioClick = e => {
    setSelectedSlot({
      name: e.target.value,
      timeLabel: e.target.name,
      startDateTime: dayjs(selectedDay.date).toISOString(),
    });
  };

  useEffect(() => {
    gtm.fire(
      testDriveDateUpdateUrlEvent({
        type: testDriveType,
        pageTitle: router.asPath,
        affiliation,
        isSSRRendered,
      })
    );
  }, []);

  useEffect(() => {
    setModifiedProgressDelay(true);
  }, [selectedDay?.date]);

  return (
    <section className="flex justify-start items-stretch w-full">
      <ProgressBar
        circleDelay={isModifiedProgressDelay && isStepComplete ? 0 : 1.2}
        isComplete={isStepComplete}
      />
      <motion.div
        key="pick-date-step"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <ContentContainer
          progressBarColor={progressBarColor}
          title={t("testDrive.dateAndHour")}
        >
          <AnimatePresence>
            {(isParkopolyEnabled && !initialDay) ||
            (!isParkopolyEnabled && isLaMaisonWithoutEmail) ? (
              <Typography className="mb-8">
                {t("testDrive.slotsUnavailable")}
              </Typography>
            ) : (
              <>
                {!isStepComplete && (
                  <div className="min-h-120">
                    <ThemeProvider theme={materialTheme}>
                      <MuiPickersUtilsProvider
                        utils={DayjsUtils}
                        locale={DAYJS_LOCALES_MAP[i18n.language]}
                      >
                        <DatePicker
                          autoOk
                          disablePast
                          disableToolbar
                          format="DD/MM/YYYY"
                          onChange={onDayChange}
                          onClose={onDatePickerClose}
                          onMonthChange={onMonthChange}
                          shouldDisableDate={shouldDisableDate}
                          TextFieldComponent={DatePickerTextField}
                          value={dayjs(selectedDay.date)}
                          variant="inline"
                        />
                      </MuiPickersUtilsProvider>
                    </ThemeProvider>
                    {isParkopolyEnabled ? (
                      <SlotPicker
                        selectedSlot={selectedSlot}
                        onSlotChange={setSelectedSlot}
                        slots={selectedDay.dailySlotPeriodList}
                      />
                    ) : (
                      <div className="flex items-center min-h-16 py-4">
                        <AnimatePresence>
                          {selectedDay?.date && (
                            <motion.div
                              animate={{ opacity: 1 }}
                              className="flex flex-wrap items-center gap-x-8 gap-y-4 min-h-16"
                              exit={{ opacity: 0 }}
                              initial={{ opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Radio
                                checked={
                                  selectedSlot?.name === VALID_TIMES.MORNING
                                }
                                data-id="test-drive-radio-button-morning"
                                disabled={getIsTimeOptionDisabled(
                                  selectedDay,
                                  VALID_TIMES.MORNING
                                )}
                                label={t("testDrive.morning")}
                                name={t("testDrive.morning")}
                                onChange={onTimeSpanRadioClick}
                                value={VALID_TIMES.MORNING}
                              />
                              <Radio
                                checked={
                                  selectedSlot?.name === VALID_TIMES.AFTERNOON
                                }
                                data-id="test-drive-radio-button-afternoon"
                                disabled={getIsTimeOptionDisabled(
                                  selectedDay,
                                  VALID_TIMES.AFTERNOON
                                )}
                                label={t("testDrive.afternoon")}
                                name={t("testDrive.afternoon")}
                                onChange={onTimeSpanRadioClick}
                                value={VALID_TIMES.AFTERNOON}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                    <RequestButton
                      data-id="test-drive-complete-button"
                      disabled={!selectedSlot}
                      onRequestSuccess={onContinueButtonClickSuccess}
                      size="large"
                      triggerRequestCallback={onContinueButtonClick}
                      variant="primary"
                    >
                      {t("testDrive.continue")}
                    </RequestButton>
                  </div>
                )}
                {isStepComplete && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.5 }}
                  >
                    {isParkopolyEnabled ? (
                      <Typography
                        className="text-lg capitalize"
                        data-id="test-drive-displayed-date"
                      >
                        {`${dayjs(selectedDay.date)
                          .locale(DAYJS_LOCALES_MAP[i18n.language])
                          .format("LL")} ${selectedSlot?.name.replace(
                          /\s/g,
                          "-"
                        ) ?? null}`}
                      </Typography>
                    ) : (
                      <Typography
                        className="text-lg capitalize"
                        data-id="test-drive-displayed-date"
                      >
                        {dayjs(selectedDay.date)
                          .locale(DAYJS_LOCALES_MAP[i18n.language])
                          .format("LL")}{" "}
                        {t(`testDrive.${selectedSlot?.name}`)}
                      </Typography>
                    )}
                    {!isFinalStepComplete && (
                      <SimpleButton
                        className="pl-0"
                        dataId="test-drive-modify-pick-date"
                        onClick={onModifyButtonClick}
                        withArrow
                      >
                        {t("general.modify")}
                      </SimpleButton>
                    )}
                  </motion.div>
                )}
              </>
            )}
          </AnimatePresence>
        </ContentContainer>
      </motion.div>
    </section>
  );
};

PickDateStep.propTypes = {
  affiliation: string,
  isFinalStepComplete: bool.isRequired,
  isStepComplete: bool.isRequired,
  progressBarColor: string,
  resetProgress: func,
  setIsStepComplete: func.isRequired,
  testDriveType: string.isRequired,
  parkopolyAvailableSlots: arrayOf(object),
  testDriveLocation: object,
  testDriveExclusionDates: arrayOf(string),
  initialSelectedSlot: object,
  isSSRRendered: bool,
};

export default PickDateStep;
